import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaQuestionCircle } from 'react-icons/fa';

/**
 * Shared help content for date/datetime filter values (literals, YYYY-MM-DD, between).
 * Used by list view filters, reports, and workflow field updates.
 *
 * @param {'filter'|'fieldUpdate'} purpose - filter (default) or workflow field update
 */
export default function DateFilterLiteralsHelpModal({ show, onHide, purpose = 'filter' }) {
  const isFieldUpdate = purpose === 'fieldUpdate';
  const title = isFieldUpdate ? 'Date Value Help' : 'Date Filter Help';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="date-help-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaQuestionCircle className="me-2 text-primary" />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6">
            <h6 className="text-primary mb-3">Date formats</h6>
            <div className="mb-3">
              <strong>Specific Dates:</strong>
              <ul className="mt-2">
                <li><code>2024-01-15</code> - January 15, 2024</li>
                <li><code>2024-12-31</code> - December 31, 2024</li>
              </ul>
            </div>

            {!isFieldUpdate && (
              <div className="mb-3">
                <strong>Between Operator:</strong>
                <ul className="mt-2">
                  <li><code>2024-01-01|2024-01-31</code> - January 2024</li>
                  <li><code>2024-06-01|2024-06-30</code> - June 2024</li>
                </ul>
              </div>
            )}
          </div>

          <div className="col-md-6">
            <h6 className="text-primary mb-3">Date literals</h6>
            <div className="mb-3">
              <strong>Basic Literals:</strong>
              <ul className="mt-2">
                <li><code>TODAY</code> - Current date</li>
                <li><code>YESTERDAY</code> - Previous day</li>
                <li><code>TOMORROW</code> - Next day</li>
              </ul>
            </div>

            <div className="mb-3">
              <strong>Week Literals:</strong>
              <ul className="mt-2">
                <li><code>THIS_WEEK</code> - Current week</li>
                <li><code>LAST_WEEK</code> - Previous week</li>
                <li><code>NEXT_WEEK</code> - Next week</li>
              </ul>
            </div>

            <div className="mb-3">
              <strong>Month Literals:</strong>
              <ul className="mt-2">
                <li><code>THIS_MONTH</code> - Current month</li>
                <li><code>LAST_MONTH</code> - Previous month</li>
                <li><code>NEXT_MONTH</code> - Next month</li>
              </ul>
            </div>

            <div className="mb-3">
              <strong>Quarter Literals:</strong>
              <ul className="mt-2">
                <li><code>THIS_QUARTER</code> - Current quarter</li>
                <li><code>LAST_QUARTER</code> - Previous quarter</li>
                <li><code>NEXT_QUARTER</code> - Next quarter</li>
              </ul>
            </div>

            <div className="mb-3">
              <strong>Year Literals:</strong>
              <ul className="mt-2">
                <li><code>THIS_YEAR</code> - Current year</li>
                <li><code>LAST_YEAR</code> - Previous year</li>
                <li><code>NEXT_YEAR</code> - Next year</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <h6 className="text-primary mb-3">N-period literals</h6>
            <div className="row">
              <div className="col-md-4">
                <strong>Days:</strong>
                <ul className="mt-2">
                  <li><code>LAST_N_DAYS:7</code> - Last 7 days</li>
                  <li><code>LAST_N_DAYS:30</code> - Last 30 days</li>
                  <li><code>NEXT_N_DAYS:7</code> - Next 7 days</li>
                  <li><code>NEXT_N_DAYS:30</code> - Next 30 days</li>
                </ul>
              </div>
              <div className="col-md-4">
                <strong>Weeks:</strong>
                <ul className="mt-2">
                  <li><code>LAST_N_WEEKS:2</code> - Last 2 weeks</li>
                  <li><code>LAST_N_WEEKS:4</code> - Last 4 weeks</li>
                  <li><code>NEXT_N_WEEKS:2</code> - Next 2 weeks</li>
                  <li><code>NEXT_N_WEEKS:4</code> - Next 4 weeks</li>
                </ul>
              </div>
              <div className="col-md-4">
                <strong>Months/Years:</strong>
                <ul className="mt-2">
                  <li><code>LAST_N_MONTHS:3</code> - Last 3 months</li>
                  <li><code>LAST_N_MONTHS:6</code> - Last 6 months</li>
                  <li><code>LAST_N_YEARS:1</code> - Last 1 year</li>
                  <li><code>LAST_N_YEARS:2</code> - Last 2 years</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-info mt-3">
          <strong>Tips:</strong>
          {isFieldUpdate ? (
            <ul className="mb-0 mt-2">
              <li>Use <code>TODAY</code>, <code>LAST_N_DAYS:7</code>, etc. for values resolved when the workflow runs</li>
              <li>Use <code>YYYY-MM-DD</code> for a fixed calendar date</li>
              <li>Use <code>{'{{field_name}}'}</code> to copy from another field — do not wrap literals in braces (use <code>TODAY</code>, not <code>{'{{TODAY}}'}</code>)</li>
              <li>All date literals are case-insensitive</li>
            </ul>
          ) : (
            <ul className="mb-0 mt-2">
              <li>Use specific dates (YYYY-MM-DD) for exact date matching</li>
              <li>Use date literals for relative date filtering</li>
              <li>For &quot;Between&quot; operator, use format: <code>start_date|end_date</code></li>
              <li>All date literals are case-insensitive</li>
            </ul>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
